import { Database } from "@/types/supabase";
import { createClient } from "@supabase/supabase-js";
import { type Session } from "@supabase/supabase-js";
import { toast } from "sonner";

export const supabaseUrl =
  import.meta.env.VITE_SUPABASE_URL || "https://kpzerpcgbdhjkcsuhbvg.supabase.co";
export const supabaseAnonKey =
  import.meta.env.VITE_SUPABASE_ANON_KEY ||
  "eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpc3MiOiJzdXBhYmFzZSIsInJlZiI6ImtwemVycGNnYmRoamtjc3VoYnZnIiwicm9sZSI6ImFub24iLCJpYXQiOjE3NDI2Njc1NDQsImV4cCI6MjA1ODI0MzU0NH0.YAqp-4eFpEqVIJgzSrRzoTky8k4DxPKbngh-AIqDEIg";

// Check for environment variables
if (!supabaseUrl || !supabaseAnonKey) {
  console.error("Missing Supabase environment variables");

  // Show a toast notification if we're in a browser environment
  if (typeof window !== "undefined") {
    toast.error("Supabase configuration is missing", {
      description: "Please ensure the environment variables are set correctly.",
      duration: 5000,
    });
  }
}

// Create the Supabase client, ensuring we never pass empty strings
export const supabase = createClient<Database>(supabaseUrl, supabaseAnonKey);

// Authentication helpers
export const auth = {
  signInWithPassword: async ({ email, password }: { email: string; password: string }) => {
    try {
      return await supabase.auth.signInWithPassword({ email, password });
    } catch (error) {
      console.error("Sign in error:", error);
      throw error;
    }
  },

  signUp: async ({
    email,
    password,
    options,
  }: {
    email: string;
    password: string;
    options?: { data?: { [key: string]: string } };
  }) => {
    try {
      return await supabase.auth.signUp({ email, password, options });
    } catch (error) {
      console.error("Sign up error:", error);
      throw error;
    }
  },

  signOut: async () => {
    try {
      return await supabase.auth.signOut();
    } catch (error) {
      console.error("Sign out error:", error);
      throw error;
    }
  },

  getSession: async () => {
    try {
      return await supabase.auth.getSession();
    } catch (error) {
      console.error("Get session error:", error);
      throw error;
    }
  },

  getUser: async () => {
    try {
      const {
        data: { user },
      } = await supabase.auth.getUser();
      return user;
    } catch (error) {
      console.error("Get user error:", error);
      throw error;
    }
  },

  onAuthStateChange: (callback: (event: string, session: Session | null) => void) => {
    try {
      return supabase.auth.onAuthStateChange((event, session) => {
        callback(event, session);
      });
    } catch (error) {
      console.error("Auth state change error:", error);
      throw error;
    }
  },
};
